import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Spinner from '../../Spinner';
import { fetchConferenceSubscriptionTeams } from '../../../services/admin/subscriptionTrackerApi';
import {
  ConferenceSubscriptionTeamItem,
  ConferenceSubscriptionTeamsResponse,
} from '../../../types/admin/subscriptionTracker';

type LoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'success'; data: ConferenceSubscriptionTeamsResponse };

const teamsCache = new Map<string, ConferenceSubscriptionTeamsResponse>();

function cacheKey(sportCode: string, conferenceName: string): string {
  return `${sportCode}::${conferenceName}`;
}

interface ConferenceDidHoverProps {
  sportCode: string;
  conferenceName: string;
  children: React.ReactNode;
}

function TeamNamesList({
  title,
  teams,
  tone,
}: {
  title: string;
  teams: ConferenceSubscriptionTeamItem[];
  tone: 'did' | 'not-did';
}) {
  return (
    <div className="min-w-0 flex-1">
      <div
        className={`px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide sticky top-0 ${
          tone === 'did' ? 'bg-green-50 text-green-800' : 'bg-slate-50 text-slate-700'
        }`}
      >
        {title} ({teams.length})
      </div>
      {teams.length === 0 ? (
        <p className="px-2 py-3 text-xs text-gray-400">None</p>
      ) : (
        <ul className="max-h-48 overflow-y-auto divide-y divide-gray-100">
          {teams.map((team, index) => (
            <li
              key={`${team.team_id ?? 'x'}-${team.team_name}-${index}`}
              className="px-2 py-1.5 text-xs text-gray-800"
            >
              <span className="font-medium">{team.team_name}</span>
              {team.team_id == null && (
                <span className="ml-1 text-[10px] text-gray-400">(unmatched)</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Hover on Subscribed/Total — fetches conference subscription-teams and shows Subscribed / Not subscribed lists.
 */
const ConferenceDidHover: React.FC<ConferenceDidHoverProps> = ({
  sportCode,
  conferenceName,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<LoadState>(() => {
    const cached = teamsCache.get(cacheKey(sportCode, conferenceName));
    return cached ? { status: 'success', data: cached } : { status: 'idle' };
  });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const tooltipId = useId();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = 440;
    const maxLeft = window.innerWidth - width - 8;
    setCoords({
      top: rect.bottom + 6,
      left: Math.max(8, Math.min(rect.left, maxLeft)),
    });
  };

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const loadTeams = () => {
    const key = cacheKey(sportCode, conferenceName);
    const cached = teamsCache.get(key);
    if (cached) {
      setState({ status: 'success', data: cached });
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState({ status: 'loading' });

    void (async () => {
      const result = await fetchConferenceSubscriptionTeams(
        {
          sport_code: sportCode,
          conference_name: conferenceName,
        },
        controller.signal
      );
      if (result.aborted || controller.signal.aborted) return;

      if (result.data) {
        teamsCache.set(key, result.data);
        setState({ status: 'success', data: result.data });
      } else {
        setState({ status: 'error', error: result.error ?? 'Failed to load teams' });
      }
    })();
  };

  const handleEnter = () => {
    clearCloseTimer();
    setOpen(true);
    updatePosition();
    loadTeams();
  };

  const handleLeave = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, state.status]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  useEffect(
    () => () => {
      clearCloseTimer();
      abortRef.current?.abort();
    },
    []
  );

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex cursor-help border-b border-dotted border-gray-400"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
        tabIndex={0}
        aria-describedby={open ? tooltipId : undefined}
      >
        {children}
      </span>
      {open &&
        coords &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            className="fixed z-[100] w-[440px] rounded-md border border-gray-200 bg-white shadow-lg"
            style={{ top: coords.top, left: coords.left }}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={handleLeave}
          >
            <div className="px-3 py-2 border-b border-gray-100 text-xs font-semibold text-gray-700 truncate">
              {conferenceName}
            </div>

            {state.status === 'idle' || state.status === 'loading' ? (
              <div className="flex items-center gap-2 px-3 py-4 text-sm text-gray-600">
                <Spinner size="sm" />
                <span>Loading teams…</span>
              </div>
            ) : state.status === 'error' ? (
              <div className="px-3 py-4 text-sm text-red-600">{state.error}</div>
            ) : (
              <div className="flex divide-x divide-gray-100">
                <TeamNamesList title="Subscribed" teams={state.data.teams_did} tone="did" />
                <TeamNamesList
                  title="Not subscribed"
                  teams={state.data.teams_not_did}
                  tone="not-did"
                />
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

export default ConferenceDidHover;
